import { MouseEvent, ReactNode, useState } from 'react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/shadcn/collapsible'
import { cn } from '@/lib/utils'
import { Checkbox } from '@/components/shadcn/checkbox'
import { IconRightChevronOutline } from '@icons/IconRightChevronOutline'
import { StatementEditorV2IndexNode } from '../../types'

export interface TreeNodeProps {
  node: StatementEditorV2IndexNode
  selectedItems: string[]
  onCheckChange: (contentIds: string[], checked: boolean) => void
  level: number
  onCheckboxClick: (e: MouseEvent) => void
}

export const getAllContentRefs = (node: StatementEditorV2IndexNode): string[] => {
  const contentRefs = node.contentRefs ? [...node.contentRefs] : []

  if (node.children && node.children.length > 0) {
    for (const child of node.children) {
      contentRefs.push(...getAllContentRefs(child))
    }
  }

  return Array.from(new Set(contentRefs))
}

function TreeNodeCheckbox({
  nodeId,
  contentRefs,
  isChecked,
  isIndeterminate,
  onCheckChange,
  onCheckboxClick,
}: {
  nodeId: string
  contentRefs: string[]
  isChecked: boolean
  isIndeterminate: boolean
  onCheckChange: (contentIds: string[], checked: boolean) => void
  onCheckboxClick: (e: MouseEvent) => void
}) {
  if (contentRefs.length === 0) {
    return null
  }

  return (
    <span className="inline-flex items-center" onClick={onCheckboxClick}>
      <Checkbox
        id={`checkbox-${nodeId}`}
        checked={isIndeterminate ? 'indeterminate' : isChecked}
        onCheckedChange={(checked) => {
          if (checked === 'indeterminate') return
          onCheckChange(contentRefs, checked)
        }}
      />
    </span>
  )
}

function TreeNodeLabel({
  title,
  isWeightSemibold,
  hasChildren,
  isOpen,
}: {
  title: string
  isWeightSemibold: boolean
  hasChildren: boolean
  isOpen?: boolean
}) {
  return (
    <div className="flex min-w-0 items-center gap-1">
      {hasChildren ? (
        <IconRightChevronOutline
          className={cn('size-3 shrink-0 transition-transform duration-200', {
            'rotate-90': isOpen,
          })}
        />
      ) : (
        <span className="size-3 shrink-0" aria-hidden />
      )}
      <span className={cn('min-w-0 whitespace-normal leading-snug', isWeightSemibold ? 'font-semibold' : '')}>
        {title}
      </span>
    </div>
  )
}

const TREE_ROW_LINE_CLASS = 'flex min-h-[1.375rem] items-center'

function TreeNodeRow({ children, checkbox }: { children: ReactNode; checkbox: React.ReactNode }) {
  return (
    <div className="flex w-full gap-2 py-1.5 pr-3">
      <div className={cn('min-w-0 flex-1', TREE_ROW_LINE_CLASS)}>{children}</div>
      {checkbox ? <div className={cn('shrink-0 self-start', TREE_ROW_LINE_CLASS)}>{checkbox}</div> : null}
    </div>
  )
}

export const TreeNode = ({ node, selectedItems, onCheckChange, onCheckboxClick, level }: TreeNodeProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const hasChildren = node.children && node.children.length > 0
  const isWeightSemibold = level === 1 // Only apply semibold to top level

  // Get all content references for this node and its descendants
  const contentRefs = getAllContentRefs(node)

  // Check if this node should be checked based on its content references
  const isChecked = contentRefs.length > 0 && contentRefs.every((id) => selectedItems.includes(id))

  // Check if this node should be indeterminate (some but not all content refs are selected)
  const isIndeterminate =
    contentRefs.length > 0 &&
    contentRefs.some((id) => selectedItems.includes(id)) &&
    !contentRefs.every((id) => selectedItems.includes(id))

  const checkbox = (
    <TreeNodeCheckbox
      nodeId={node.id}
      contentRefs={contentRefs}
      isChecked={isChecked}
      isIndeterminate={isIndeterminate}
      onCheckChange={onCheckChange}
      onCheckboxClick={onCheckboxClick}
    />
  )

  if (!hasChildren) {
    return (
      <div role="treeitem" className="max-w-96 rounded text-foreground hover:bg-muted hover:text-foreground">
        <TreeNodeRow checkbox={checkbox}>
          <TreeNodeLabel title={node.title} isWeightSemibold={isWeightSemibold} hasChildren={false} />
        </TreeNodeRow>
      </div>
    )
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div
        role="treeitem"
        aria-expanded={isOpen}
        className="max-w-96 rounded text-foreground hover:bg-muted hover:text-foreground"
      >
        <TreeNodeRow checkbox={checkbox}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className={cn('w-full text-left text-foreground hover:text-foreground', TREE_ROW_LINE_CLASS)}
            >
              <TreeNodeLabel title={node.title} isWeightSemibold={isWeightSemibold} hasChildren isOpen={isOpen} />
            </button>
          </CollapsibleTrigger>
        </TreeNodeRow>
      </div>

      <CollapsibleContent className="overflow-hidden pl-4 transition-all data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        <div role="group" className="flex flex-col gap-y-0.5 pt-0.5">
          {node.children?.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              selectedItems={selectedItems}
              onCheckChange={onCheckChange}
              onCheckboxClick={onCheckboxClick}
              level={level + 1}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
